/**
 * Silo-scoped available-language resolution: the `siloLanguagesConfig` from
 * `psi.config` mapped through hooks and helpers.
 *
 * Each silo lists which ISO locales it supports; if no list is configured, the
 * `default` entry of `siloLanguagesConfig` is used, and if that's also empty,
 * English alone. `useFilteredLanguageOptions` narrows the full
 * `ALL_LANGUAGE_OPTIONS` list (built once from `languageDefinitions`) to the
 * silo's subset, which is what language pickers should call. `getLanguageLabel`
 * falls back to the default label rather than throwing on unknown locales.
 *
 * `isLanguageModerationAvailableForSilo` is the single gate for moderation's
 * language features, so the queue filters and the Account picker that seeds
 * them can never disagree about whether the feature exists for a silo.
 */

import { DEFAULT_ISO_LOCALE, type IsoLocale } from '@repo/shared/locale';
import { siloLanguagesConfig } from 'psi.config';
import { useMemo } from 'react';
import { useDatastore } from 'util/datastore';
import type { LanguageDefinition } from 'util/locale';
import { languageDefinitions } from 'util/locale';
import type { TLabel } from 'util/stdtypes';
import { t } from 'util/stdtypes';
import { getDeploymentConfig } from 'util/util';

export type LanguageOption = {
    key: IsoLocale;
    label: TLabel;
    emoji?: string;
};

const LANGUAGE_ENTRIES = Object.entries(languageDefinitions) as Array<[IsoLocale, LanguageDefinition]>;

const DEFAULT_LANGUAGE_LABEL: TLabel =
    (languageDefinitions as Record<string, LanguageDefinition | undefined>)[DEFAULT_ISO_LOCALE]?.label ?? t('language.en-US', 'English');

const ALL_LANGUAGE_OPTIONS: LanguageOption[] = LANGUAGE_ENTRIES.map(([key, meta]) => ({
    key,
    label: meta.label,
    emoji: meta.emoji,
}));

function resolveAvailableLanguages(siloKey: string | null | undefined): IsoLocale[] {
    // siloLanguagesConfig values come from psi.config.ts and are typed
    // IsoLocale[].
    const rawForSilo = siloKey ? siloLanguagesConfig[siloKey] : undefined;
    const rawDefault = siloLanguagesConfig.default ?? [];
    const raw = rawForSilo && rawForSilo.length > 0 ? rawForSilo : rawDefault;

    if (raw.length === 0) {
        return [DEFAULT_ISO_LOCALE];
    }

    return raw;
}

function useAvailableLanguages(): IsoLocale[] {
    const datastore = useDatastore();
    const siloKey = datastore.getSiloKey();

    return resolveAvailableLanguages(siloKey);
}

export function getAvailableLanguagesForSilo(siloKey: string | null | undefined): IsoLocale[] {
    return resolveAvailableLanguages(siloKey);
}

export function useIsLanguageAvailable(locale: IsoLocale | null | undefined): boolean {
    const availableLanguages = useAvailableLanguages();
    if (!locale) return false;
    return availableLanguages.includes(locale);
}

function useFilteredLanguageOptions(options: LanguageOption[] = ALL_LANGUAGE_OPTIONS): LanguageOption[] {
    const availableLanguages = useAvailableLanguages();

    return useMemo(() => options.filter((option) => availableLanguages.includes(option.key)), [options, availableLanguages]);
}

export function useAvailableLanguageOptions(): LanguageOption[] {
    return useFilteredLanguageOptions();
}

export function getLanguageLabel(locale: string): TLabel {
    const meta = (languageDefinitions as Record<string, LanguageDefinition | undefined>)[locale];
    if (!meta) {
        return DEFAULT_LANGUAGE_LABEL;
    }

    return meta.label;
}

/**
 * Whether the silo's own config declares it multilingual (`multiLanguage` in
 * `psi.config`). Canonical read of that flag — `content-translation`'s
 * `useIsMultiLanguageSilo` delegates here.
 */
export function isMultiLanguageSiloKey(siloKey: string | null | undefined): boolean {
    if (!siloKey) return false;
    return getDeploymentConfig()?.siloConfig?.[siloKey]?.multiLanguage === true;
}

/**
 * Whether moderation's language features apply to this silo: the queue's
 * language prefilters and the Account picker that seeds them.
 *
 * Every language-moderation surface reads this one gate (filter creation, the
 * filter UI, and the Account picker), so they cannot disagree about whether the
 * feature exists. A silo qualifies when it declares itself multilingual and
 * offers more than one language; `siloLanguagesConfig` listing two locales is
 * not on its own enough, since several single-language silos list variants they
 * never mix.
 */
export function isLanguageModerationAvailableForSilo(siloKey: string | null | undefined): boolean {
    return isMultiLanguageSiloKey(siloKey) && resolveAvailableLanguages(siloKey).length > 1;
}

export function useIsLanguageModerationAvailable(): boolean {
    const siloKey = useDatastore().getSiloKey();
    return isLanguageModerationAvailableForSilo(siloKey);
}
